import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Tag, Calendar, Percent, Gift, Euro, DollarSign } from 'lucide-react';
import apiService from '../services/api';
import Modal from './Modal';
import {
  getClientPromotionDisplayStatus,
  getClientPromotionUsageDetail,
} from '../utils/clientPromotionDisplayStatus';

const ClientPromotions = ({ clientId, clientName, commercantId: commercantIdProp }) => {
  const [promotions, setPromotions] = useState([]);
  const [commercants, setCommercants] = useState([]);
  const [filteredCommercants, setFilteredCommercants] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newPromotion, setNewPromotion] = useState({
    userId: clientId,
    commercantId: '',
    titre: '',
    description: '',
    typeReduction: 'POURCENTAGE',
    reduction: '',
    montantMinimum: '',
    codePromo: '',
    dateDebut: '',
    dateFin: '',
    actif: true,
    typeRecurrence: 'PONCTUELLE'
  });

  useEffect(() => {
    if (clientId) {
      // Récupérer l'utilisateur actuel pour vérifier les permissions
      try {
        const userData = localStorage.getItem('admin_user');
        if (userData) {
          const user = JSON.parse(userData);
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('Erreur parsing user data:', error);
      }
      
      fetchPromotions();
      fetchCommercants();
    }
  }, [clientId]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      setError('');
      const promotionsData = await apiService.getClientPromotions(clientId, commercantIdProp);
      const promotionsArray = Array.isArray(promotionsData) ? promotionsData : [];
      
      console.log('📋 [ClientPromotions] Promotions chargées:', promotionsArray.map(p => ({
        id: p.id,
        titre: p.titre,
        commercantId: p.commercantId,
        commercantNom: p.commercant?.nom
      })));
      
      setPromotions(promotionsArray);
    } catch (err) {
      console.error('Erreur récupération promotions client:', err);
      setError('Erreur lors du chargement des promotions');
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommercants = async () => {
    try {
      const commercantsData = await apiService.getCommercants();
      const allCommercants = Array.isArray(commercantsData?.data) ? commercantsData.data : 
                           Array.isArray(commercantsData) ? commercantsData : [];
      setCommercants(allCommercants);
      
      // Filtrer selon le rôle
      if (currentUser) {
        if (currentUser.role === 'SUPER_ADMIN' || currentUser.role === 'ADMIN') {
          // SUPER_ADMIN et ADMIN voient tous les commerçants
          setFilteredCommercants(allCommercants);
        } else if (currentUser.role === 'COMMERCANT' && currentUser.commercantId) {
          // COMMERCANT voit uniquement son propre commerce
          const ownCommercant = allCommercants.find(c => 
            c.id === currentUser.commercantId || 
            c.id === currentUser.commercant?.id
          );
          if (ownCommercant) {
            setFilteredCommercants([ownCommercant]);
            // Pré-sélectionner le commerce du commerçant
            setNewPromotion(prev => ({
              ...prev,
              commercantId: ownCommercant.id
            }));
          } else {
            setFilteredCommercants([]);
          }
        } else {
          setFilteredCommercants([]);
        }
      } else {
        // Si pas d'utilisateur, afficher tous (sera filtré après chargement)
        setFilteredCommercants(allCommercants);
      }
    } catch (err) {
      console.error('Erreur récupération commerçants:', err);
      setFilteredCommercants([]);
    }
  };
  
  // Re-filtrer les commerçants quand currentUser change
  useEffect(() => {
    if (currentUser && commercants.length > 0) {
      if (currentUser.role === 'SUPER_ADMIN' || currentUser.role === 'ADMIN') {
        setFilteredCommercants(commercants);
      } else if (currentUser.role === 'COMMERCANT' && currentUser.commercantId) {
        const ownCommercant = commercants.find(c => 
          c.id === currentUser.commercantId || 
          c.id === currentUser.commercant?.id
        );
        if (ownCommercant) {
          setFilteredCommercants([ownCommercant]);
          setNewPromotion(prev => ({
            ...prev,
            commercantId: ownCommercant.id
          }));
        } else {
          setFilteredCommercants([]);
        }
      }
    }
  }, [currentUser, commercants]);

  const handleEdit = (promotion) => {
    console.log('🔍 [ClientPromotions] handleEdit appelé avec:', {
      promotionId: promotion.id,
      titre: promotion.titre,
      commercantId: promotion.commercantId,
      commercantNom: promotion.commercant?.nom
    });
    
    // S'assurer qu'on a bien l'ID de la promotion
    if (!promotion.id) {
      console.error('❌ [ClientPromotions] Promotion sans ID!', promotion);
      alert('Erreur: Promotion sans ID');
      return;
    }
    
    setSelectedPromotion(promotion);
    setNewPromotion({
      userId: clientId,
      commercantId: promotion.commercantId || '',
      titre: promotion.titre || '',
      description: promotion.description || '',
      typeReduction: promotion.typeReduction || 'POURCENTAGE',
      reduction: promotion.reduction?.toString() || '',
      montantMinimum: promotion.montantMinimum?.toString() || '',
      codePromo: promotion.codePromo || '',
      dateDebut: promotion.dateDebut ? new Date(promotion.dateDebut).toISOString().split('T')[0] : '',
      dateFin: promotion.dateFin ? new Date(promotion.dateFin).toISOString().split('T')[0] : '',
      actif: promotion.actif !== undefined ? promotion.actif : true,
      typeRecurrence: promotion.typeRecurrence || 'PONCTUELLE'
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (promotion) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la promotion "${promotion.titre}" ?`)) {
      return;
    }

    try {
      await apiService.deleteClientPromotion(promotion.id);
      await fetchPromotions();
      alert('Promotion supprimée avec succès');
    } catch (error) {
      console.error('Erreur suppression promotion:', error);
      alert(`Erreur lors de la suppression: ${error.message || error}`);
    }
  };

  const handleCreatePromotion = async (e) => {
    e.preventDefault();
    
    if (!newPromotion.titre || !newPromotion.commercantId || !newPromotion.dateDebut) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setIsCreating(true);
    setError('');
    
    try {
      // Pour les promotions permanentes, pas de code promo (s'applique automatiquement)
      const codePromoFinal = (newPromotion.typeRecurrence === 'PERMANENTE') ? null :
        (newPromotion.codePromo || 
        `${newPromotion.titre.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 8)}${Date.now().toString().slice(-4)}`);

      // Pour les promotions permanentes, codePromo doit être null
      // Pour les autres, on envoie le codePromo (généré ou fourni)
      const codePromoValue = (newPromotion.typeRecurrence === 'PERMANENTE') ? null : codePromoFinal;

      if (selectedPromotion && selectedPromotion.id) {
        console.log('✏️ [ClientPromotions] Modification promotion:', {
          selectedPromotionId: selectedPromotion.id,
          selectedPromotionTitre: selectedPromotion.titre,
          selectedPromotionCommercantId: selectedPromotion.commercantId,
          newCommercantId: newPromotion.commercantId,
          newTitre: newPromotion.titre
        });
        
        // Pour la modification, on ne doit PAS envoyer userId et commercantId
        // car ils ne peuvent pas être modifiés
        // S'assurer que dateDebut est une string au format YYYY-MM-DD
        let dateDebutValue = newPromotion.dateDebut;
        if (!dateDebutValue || typeof dateDebutValue !== 'string') {
          dateDebutValue = new Date().toISOString().split('T')[0];
        }
        // Si c'est un objet Date, le convertir en string
        if (dateDebutValue instanceof Date) {
          dateDebutValue = dateDebutValue.toISOString().split('T')[0];
        }

        const updateData = {
          titre: newPromotion.titre,
          description: newPromotion.description,
          typeReduction: newPromotion.typeReduction || 'POURCENTAGE',
          reduction: parseFloat(newPromotion.reduction) || (newPromotion.typeReduction === 'MONTANT_FIXE' ? 0 : 1),
          montantMinimum: newPromotion.montantMinimum ? parseFloat(newPromotion.montantMinimum) : null,
          codePromo: codePromoValue,
          dateDebut: dateDebutValue,
          dateFin: newPromotion.dateFin || null,
          actif: newPromotion.actif,
          typeRecurrence: newPromotion.typeRecurrence || 'PONCTUELLE'
        };
        
        // Vérification critique : s'assurer que l'ID est bien celui de la promotion sélectionnée
        const promotionIdAModifier = selectedPromotion.id;
        
        console.log('📤 [ClientPromotions] Envoi mise à jour:', {
          promotionId: promotionIdAModifier,
          selectedPromotionTitre: selectedPromotion.titre,
          selectedPromotionCommercantId: selectedPromotion.commercantId,
          updateData
        });
        
        // Vérification finale avant envoi
        if (!promotionIdAModifier) {
          console.error('❌ [ClientPromotions] ERREUR CRITIQUE: Pas d\'ID de promotion!', selectedPromotion);
          alert('Erreur: Impossible de modifier la promotion (ID manquant)');
          return;
        }
        
        await apiService.updateClientPromotion(promotionIdAModifier, updateData);
      } else {
        // Pour la création, on envoie tous les champs
        const descriptionTrimmed = String(newPromotion.description || '').trim();
        const descriptionForApi =
          descriptionTrimmed.length >= 5
            ? descriptionTrimmed
            : `${newPromotion.titre} — offre personnelle`.trim().slice(0, 500);

        const createData = {
          userId: clientId,
          commercantId: commercantIdProp || newPromotion.commercantId,
          titre: newPromotion.titre,
          description: descriptionForApi,
          typeReduction: newPromotion.typeReduction || 'POURCENTAGE',
          reduction: parseFloat(newPromotion.reduction) || (newPromotion.typeReduction === 'MONTANT_FIXE' ? 0 : 1),
          montantMinimum: newPromotion.montantMinimum ? parseFloat(newPromotion.montantMinimum) : null,
          codePromo: codePromoValue,
          dateDebut: newPromotion.dateDebut || new Date().toISOString().split('T')[0],
          dateFin: newPromotion.dateFin || null,
          actif: newPromotion.actif,
          typeRecurrence: newPromotion.typeRecurrence || 'PONCTUELLE'
        };
        await apiService.createClientPromotion(createData);
        await fetchPromotions();
        setShowCreateModal(false);
        setSelectedPromotion(null);
        setNewPromotion({
          userId: clientId,
          commercantId: currentUser?.role === 'COMMERCANT' && currentUser?.commercantId ?
            (filteredCommercants[0]?.id || '') : '',
          titre: '',
          description: '',
          typeReduction: 'POURCENTAGE',
          reduction: '',
          montantMinimum: '',
          codePromo: '',
          dateDebut: '',
          dateFin: '',
          actif: true,
          typeRecurrence: 'PONCTUELLE'
        });
        return;
      }

      await fetchPromotions();
      setShowCreateModal(false);
      setSelectedPromotion(null);
      setNewPromotion({
        userId: clientId,
        commercantId: currentUser?.role === 'COMMERCANT' && currentUser?.commercantId ? 
          (filteredCommercants[0]?.id || '') : '',
        titre: '',
        description: '',
        typeReduction: 'POURCENTAGE',
        reduction: '',
        montantMinimum: '',
        codePromo: '',
        dateDebut: '',
        dateFin: '',
        actif: true,
        typeRecurrence: 'PONCTUELLE'
      });
    } catch (error) {
      console.error('Erreur création/modification promotion:', error);
      setError(`Erreur: ${error.message || error}`);
    } finally {
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Tag className="h-5 w-5 mr-2" />
          Promotions attribuées pour {clientName}
        </h3>
        <button
          onClick={() => {
            setSelectedPromotion(null);
            const defaultCommercantId =
              commercantIdProp ||
              (currentUser?.role === 'COMMERCANT' &&
                (currentUser.commercantId ||
                  currentUser.commercant?.id ||
                  filteredCommercants[0]?.id)) ||
              (filteredCommercants.length === 1 ? filteredCommercants[0].id : '') ||
              '';
            setNewPromotion({
              userId: clientId,
              commercantId: defaultCommercantId || '',
              titre: '',
              description: '',
              typeReduction: 'POURCENTAGE',
              reduction: '',
              montantMinimum: '',
              codePromo: '',
              dateDebut: '',
              dateFin: '',
              actif: true,
              typeRecurrence: 'PONCTUELLE'
            });
            setShowCreateModal(true);
          }}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Créer une promotion
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {promotions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune promotion personnalisée pour ce client</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Titre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Commerçant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Réduction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code promo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promotions.map((promotion) => (
                <tr key={promotion.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {promotion.titre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promotion.commercant?.nom || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promotion.typeReduction === 'MONTANT_FIXE' ? (
                      <span className="flex items-center">
                        <Euro className="h-4 w-4 mr-1" />
                        {promotion.reduction}€
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Percent className="h-4 w-4 mr-1" />
                        {promotion.reduction}%
                      </span>
                    )}
                    {promotion.montantMinimum && (
                      <span className="block text-xs text-gray-500 mt-1">
                        Min: {promotion.montantMinimum}€
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {promotion.typeRecurrence === 'PERMANENTE' ? (
                      <span className="text-blue-600 font-medium">Auto (permanente)</span>
                    ) : (
                      promotion.codePromo || 'N/A'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div>{new Date(promotion.dateDebut).toLocaleDateString('fr-FR')}</div>
                      {promotion.dateFin && (
                        <div className="text-gray-500">
                          → {new Date(promotion.dateFin).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promotion.typeRecurrence || 'PONCTUELLE'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const status = getClientPromotionDisplayStatus(promotion);
                      const usage = getClientPromotionUsageDetail(promotion);
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span
                            className={`inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full ${status.className}`}
                          >
                            {status.label}
                          </span>
                          {usage && (
                            <span className="text-xs text-gray-500 leading-snug">
                              {usage.dateTimeLabel}
                              <span className="block">
                                {usage.originLabel === 'Commande' ? 'Commande' : 'RDV'}
                                {usage.referenceLabel ? ` · ${usage.referenceLabel}` : ''}
                              </span>
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(promotion)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(promotion)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedPromotion(null);
        }}
        title={selectedPromotion ? "✏️ Modifier la promotion" : "🎯 Créer une promotion personnalisée"}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleCreatePromotion} className="space-y-4">
          {selectedPromotion && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Modification de:</strong> {selectedPromotion.titre} (ID: {selectedPromotion.id})
                <br />
                <strong>Commerçant actuel:</strong> {selectedPromotion.commercant?.nom || 'N/A'}
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commerçant *</label>
            <select
              value={newPromotion.commercantId}
              onChange={(e) => {
                console.log('🔄 [ClientPromotions] Changement commercantId:', {
                  ancien: newPromotion.commercantId,
                  nouveau: e.target.value,
                  selectedPromotionId: selectedPromotion?.id,
                  selectedPromotionTitre: selectedPromotion?.titre
                });
                setNewPromotion({...newPromotion, commercantId: e.target.value});
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={
                !!selectedPromotion ||
                Boolean(commercantIdProp) ||
                (currentUser?.role === 'COMMERCANT' && filteredCommercants.length === 1)
              }
            >
              <option value="">Sélectionner un commerçant</option>
              {filteredCommercants.map(commercant => (
                <option key={commercant.id} value={commercant.id}>
                  {commercant.nom}
                </option>
              ))}
            </select>
            {currentUser?.role === 'COMMERCANT' && filteredCommercants.length === 1 && (
              <p className="mt-1 text-xs text-gray-500">Votre commerce (non modifiable)</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input
              type="text"
              value={newPromotion.titre}
              onChange={(e) => setNewPromotion({...newPromotion, titre: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newPromotion.description}
              onChange={(e) => setNewPromotion({...newPromotion, description: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de réduction *</label>
            <select
              value={newPromotion.typeReduction}
              onChange={(e) => setNewPromotion({...newPromotion, typeReduction: e.target.value, reduction: ''})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="POURCENTAGE">Pourcentage (%)</option>
              <option value="MONTANT_FIXE">Montant fixe (€)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {newPromotion.typeReduction === 'MONTANT_FIXE' ? 'Montant de réduction (€) *' : 'Réduction (%) *'}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {newPromotion.typeReduction === 'MONTANT_FIXE' ? (
                  <Euro className="h-5 w-5 text-gray-400" />
                ) : (
                  <Percent className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                type="number"
                min={newPromotion.typeReduction === 'MONTANT_FIXE' ? "0.01" : "1"}
                max={newPromotion.typeReduction === 'MONTANT_FIXE' ? undefined : "100"}
                step={newPromotion.typeReduction === 'MONTANT_FIXE' ? "0.01" : "1"}
                value={newPromotion.reduction}
                onChange={(e) => setNewPromotion({...newPromotion, reduction: e.target.value})}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                required
                placeholder={newPromotion.typeReduction === 'MONTANT_FIXE' ? "5.00" : "10"}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant minimum de commande (€)</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Euro className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newPromotion.montantMinimum}
                onChange={(e) => setNewPromotion({...newPromotion, montantMinimum: e.target.value})}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                placeholder="Optionnel (ex: 20.00)"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">La promotion ne s'appliquera que si le montant de la commande est supérieur ou égal à ce montant</p>
          </div>
          {newPromotion.typeRecurrence !== 'PERMANENTE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code promo</label>
              <input
                type="text"
                value={newPromotion.codePromo}
                onChange={(e) => setNewPromotion({...newPromotion, codePromo: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Généré automatiquement si vide"
              />
              <p className="mt-1 text-xs text-gray-500">Non nécessaire pour les promotions permanentes</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de début *</label>
              <input
                type="date"
                value={newPromotion.dateDebut}
                onChange={(e) => setNewPromotion({...newPromotion, dateDebut: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin {newPromotion.typeRecurrence === 'PERMANENTE' && '(optionnel)'}
              </label>
              <input
                type="date"
                value={newPromotion.dateFin}
                onChange={(e) => setNewPromotion({...newPromotion, dateFin: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={newPromotion.typeRecurrence === 'PERMANENTE'}
              />
              {newPromotion.typeRecurrence === 'PERMANENTE' && (
                <p className="mt-1 text-xs text-gray-500">Les promotions permanentes restent actives jusqu'à désactivation manuelle</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de récurrence</label>
            <select
              value={newPromotion.typeRecurrence}
              onChange={(e) => {
                const newType = e.target.value;
                const aujourdhui = new Date().toISOString().split('T')[0];
                setNewPromotion({
                  ...newPromotion, 
                  typeRecurrence: newType,
                  // Si PERMANENTE, vider le code promo et mettre la date de début à aujourd'hui
                  codePromo: newType === 'PERMANENTE' ? '' : newPromotion.codePromo,
                  dateDebut: newType === 'PERMANENTE' ? aujourdhui : newPromotion.dateDebut
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="PONCTUELLE">Ponctuelle</option>
              <option value="HEBDOMADAIRE">Hebdomadaire</option>
              <option value="MENSUELLE">Mensuelle</option>
              <option value="PERMANENTE">Permanente (s'applique automatiquement)</option>
            </select>
            {newPromotion.typeRecurrence === 'PERMANENTE' && (
              <p className="mt-1 text-xs text-blue-600">
                ℹ️ Les promotions permanentes s'appliquent automatiquement sans code promo
              </p>
            )}
          </div>
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newPromotion.actif}
                onChange={(e) => setNewPromotion({...newPromotion, actif: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Actif</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowCreateModal(false);
                setSelectedPromotion(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isCreating ? 'Enregistrement...' : selectedPromotion ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClientPromotions;

